/**
 * Exibe um aviso quando o período de avaliação está próximo do fim.
 * Lê trial_ends_at da propriedade e mostra um aviso que pode ser dispensado.
 */

import { useState } from "react";
import { X, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { differenceInDays, parseISO } from "date-fns";

interface TrialBannerProps {
  trialEndsAt: string | null | undefined;
}

export function TrialBanner({ trialEndsAt }: TrialBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (!trialEndsAt || dismissed) return null;

  const endsAt = parseISO(trialEndsAt);
  const daysLeft = differenceInDays(endsAt, new Date());

  // Exibe apenas quando faltam até sete dias ou quando já expirou
  if (daysLeft > 7) return null;

  const expired = daysLeft < 0;

  return (
    <div className={cn(
      "w-full py-2 px-4 flex items-center justify-between text-sm font-medium",
      expired
        ? "bg-red-600 text-white"
        : daysLeft <= 2
          ? "bg-amber-600 text-white"
          : "bg-amber-500/20 text-amber-300 border-b border-amber-500/30",
    )}>
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 shrink-0" />
        {expired
          ? "Seu trial expirou. Assine um plano para continuar usando o Innsight PMS."
          : daysLeft === 0
            ? "Seu trial expira hoje! Assine um plano para não perder o acesso."
            : `Seu trial expira em ${daysLeft} dia${daysLeft !== 1 ? "s" : ""}. Assine agora para continuar.`}
        <a
          href="/planos"
          className="underline font-bold hover:no-underline ml-1"
        >
          Ver planos
        </a>
      </div>
      {!expired && (
        <button
          onClick={() => setDismissed(true)}
          aria-label="Fechar aviso do período de avaliação"
          className="p-1 rounded hover:bg-black/20 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
